import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {OfferDataManagementConfigurationStep, Range, RangeStatus} from '../../../../shared/model/range-model';
import {OfferDataManagementService} from '../../offer-data-management.service';
import {
  BslRangeCharacteristicsSummary,
  BslRangeCharacteristicsSummaryMap
} from '../../../shared/bsl-reference/offer-data-management-model';
import * as _ from 'lodash';
import {MessageService} from 'primeng/api';
import {HttpResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {RangeType} from "../../../../shared/model/range-type";

@Component({
  standalone: false,
  selector: 'app-odm-add-offer',
  templateUrl: './odm-add-offer.component.html',
  styleUrls: ['./odm-add-offer.component.less'],
})
export class OdmAddOfferComponent implements OnInit {

  @Input()
  selectedRange: Range;

  @Output()
  clickBackFromConf: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  rangeIsPublished: EventEmitter<Range> = new EventEmitter<Range>();

  stepEnum = OfferDataManagementConfigurationStep;

  currentStep: OfferDataManagementConfigurationStep;

  stepList: any[];

  activePrevious: boolean;

  activeNext: boolean;

  hideNext: boolean;

  bslCharacteristics: BslRangeCharacteristicsSummary[];

  bslCharacteristicsMap = new BslRangeCharacteristicsSummaryMap();

  bslCharacteristicsLoading: boolean;

  isModificationPending = false;

  helpMessage: string | null = null;

  constructor(private readonly offerDataManagementService: OfferDataManagementService,
              private messageService: MessageService,
              private readonly translate: TranslateService) {
  }

  ngOnInit() {
    if (this.selectedRange.status === RangeStatus.CONFIGURED ||
      this.selectedRange.status === RangeStatus.TEST || this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE) {
      if (this.selectedRange.offerDataManagementConfigurationStep) {
        if (this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE &&
          parseInt(this.selectedRange.offerDataManagementConfigurationStep.toString(), 10) === OfferDataManagementConfigurationStep.DOCUMENT) {
            this.selectedRange.offerDataManagementConfigurationStep = OfferDataManagementConfigurationStep.REFERENCE;
            this.currentStep = OfferDataManagementConfigurationStep.REFERENCE;
        } else {
            this.currentStep = parseInt(this.selectedRange.offerDataManagementConfigurationStep.toString(), 10);
        }
      } else {
        this.selectedRange.offerDataManagementConfigurationStep = OfferDataManagementConfigurationStep.CHARACTERISTIC;
        this.currentStep = OfferDataManagementConfigurationStep.CHARACTERISTIC;
      }
    }
    this.getStepList();
    this.getStepHelpMessage();
    this.activePrevious = true;
    this.hideNext = this.currentStep === OfferDataManagementConfigurationStep.PUBLICATION;
    // retrieve all characteristics from a range in bsl
    this.bslCharacteristicsLoading = this.selectedRange.bslRangeId !== null;
    const observableGetRangeMethod = this.selectedRange.bslRangeId === null
      ? this.offerDataManagementService.getCharacteristicsByRangeId(this.selectedRange.id)
      : this.offerDataManagementService.getAllCharacteristicsForRange(this.selectedRange.bslRangeId);
    observableGetRangeMethod.subscribe(
      (event: HttpResponse<any>) => {
        if (event instanceof HttpResponse) {
          this.bslCharacteristicsLoading = false;
          this.bslCharacteristics = this.formatCharacteristicValueLineBreak(event.body).sort((a, b) => a.name.localeCompare(b.name));
          this.bslCharacteristicsMap = _.keyBy(this.bslCharacteristics, 'key');
          this.selectedRange.electricalCharacteristicsFilters =
            this.selectedRange.electricalCharacteristicsFilters.filter(characteristic => {
              const key = this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE ? characteristic : characteristic.substring(this.selectedRange.nameKey.length + 1);
              return _.find(_.values(this.bslCharacteristics.map(c=> c.key)), characteristic => characteristic === key);
            });
          this.selectedRange.visibleComponentsFilters =
            this.selectedRange.visibleComponentsFilters.filter(characteristic => {
              const key = this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE ? characteristic : characteristic.substring(this.selectedRange.nameKey.length + 1);
              return _.find(_.values(this.bslCharacteristics.map(c=> c.key)), characteristic => characteristic === key);
            });
          this.selectedRange.mainCharacteristics =
            this.selectedRange.mainCharacteristics.filter(characteristic => {
              const key = this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE ? characteristic : characteristic.substring(this.selectedRange.nameKey.length + 1);
              return _.find(_.values(this.bslCharacteristics.map(c=> c.key)), characteristic => characteristic === key);
            });
          this.offerDataManagementService.updateRange(this.selectedRange).subscribe(range => this.selectedRange = range);
          this.activeNext = this.isNextActive();
        }
      },
      (error: any) => this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('T_ERROR'),
        detail: this.translate.instant(error),
      })
    );
  }

  /**
   * Function that format the [RETURN] string to line break
   * @param characList list of characteristics' return by bsl
   */
  formatCharacteristicValueLineBreak(characList: BslRangeCharacteristicsSummary[]): BslRangeCharacteristicsSummary[] {
    return characList.map(charac => {
      charac.values = charac.values.map(val => val.replace(new RegExp('\\[RETURN]', 'g'), '\n'));
      return charac;
    });
  }

  /**
   * Set the current step to a previous step
   */
  loadPreviousStep() {
    this.isModificationPending = false;
    if (this.currentStep > 0) {

      this.currentStep = this.getPreviousStep(this.currentStep, this.selectedRange.rangeType);
      this.getStepHelpMessage();
      this.getStepList();
      this.activeNext = this.currentStep !== OfferDataManagementConfigurationStep.PUBLICATION;
      this.hideNext = this.currentStep === OfferDataManagementConfigurationStep.PUBLICATION;
      this.offerDataManagementService.getRange(this.selectedRange.id).subscribe(range => this.selectedRange = range);
    } else if (this.currentStep === 0) {
      this.activeNext = true;
      this.clickBackFromConf.emit();
    }
  }

  /**
   * Range must be updated
   * @param range
   */
  updateCurrentRange(range: Range) {
    this.rangeIsPublished.emit(range);
  }

  /**
   * Set current step to next step
   */
  loadNextStep(event: any) {
    this.isModificationPending = false;
    // update the selectedRange with the current step
    if (event) {
      if (OfferDataManagementConfigurationStep.CHARACTERISTIC === this.currentStep) {
        // reset the filters which cannot be the same as the electrical characteristics
        this.selectedRange.visibleComponentsFilters = this.selectedRange.visibleComponentsFilters
          .filter(filters => !this.selectedRange.electricalCharacteristicsFilters.includes(filters));
      }
      this.selectedRange.offerDataManagementConfigurationStep = this.getEnumStep(this.currentStep + 1);
      this.offerDataManagementService.updateRange(this.selectedRange).subscribe(range => this.selectedRange = range);
    } else if (this.currentStep < this.selectedRange.offerDataManagementConfigurationStep) {
      this.offerDataManagementService.getRange(this.selectedRange.id).subscribe(range => this.selectedRange = range);
    } else if (this.currentStep >= this.selectedRange.offerDataManagementConfigurationStep) {
      // stay on the same step if no save and the current step >= on selectedRange step
      return;
    }
    // get the step list
    if (this.currentStep < 5) {

      this.currentStep = this.getNextStep(this.currentStep, this.selectedRange.rangeType);
      this.getStepHelpMessage();
      this.getStepList();
      // update next button state
      this.activeNext = this.isNextActive();
    }
    if (this.currentStep === OfferDataManagementConfigurationStep.PUBLICATION) {
      this.hideNext = true;
    }
  }

  /**
   * Check if the CharacteristicsMap is empty
   * @param obj
   */
  isEmptyCharacteristicsMapOnFilterElectricalOrMainCharacteristics(obj: any) {
    return _.isEmpty(obj) && (this.isCharacteristicStep());
  }

  /**
   * function to know if we are on a characteristics' step
   */
  isCharacteristicStep() {
    return this.currentStep === this.stepEnum.MAIN_CHARACTERISTIC || this.currentStep === this.stepEnum.CHARACTERISTIC || this.currentStep === this.stepEnum.FILTER;
  }

  /**
   * Get the selected range from db
   */
  getSelectedRange() {
    this.offerDataManagementService.getRange(this.selectedRange.id).subscribe(range => this.selectedRange = range);
  }

  /**
   * Updates Next button state
   */
  updateNextButtonState() {
    this.isModificationPending = this.currentStep < this.selectedRange.offerDataManagementConfigurationStep;
    this.activeNext = this.isNextActive();
  }

  /**
   * Updates Next button state from the reference step
   */
  updateNextButton(event: boolean) {
    this.isModificationPending = false;
    this.activeNext = event;
  }

  /**
   * Set the help message according to the current step
   */
  private getStepHelpMessage() {
    switch (this.currentStep) {
      case OfferDataManagementConfigurationStep.CHARACTERISTIC:
        this.helpMessage = 'T_OFFER_DATA_MANAGEMENT_SELECT_CHARACTERISTIC_MESSAGE';
        break;
      case OfferDataManagementConfigurationStep.FILTER:
        this.helpMessage = 'T_OFFER_DATA_MANAGEMENT_SELECT_FILTERS_MESSAGE';
        break;
      case OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC:
        this.helpMessage = 'T_OFFER_DATA_MANAGEMENT_MAIN_CHARACTERISTICS_TITLE';
        break;
      case OfferDataManagementConfigurationStep.DOCUMENT:
        this.helpMessage = !this.isBudgetaryQuoteRange() ? 'T_OFFER_DATA_MANAGEMENT_DOCUMENTS_TITLE' : null;
        break;
      default:
        this.helpMessage = null;
        break;
    }
  }

  /**
   * Check if the Next button is active
   */
  private isNextActive() {
    if (this.bslCharacteristicsLoading) {
      return false;
    }
    switch (this.currentStep) {
      case OfferDataManagementConfigurationStep.CHARACTERISTIC:
        return (this.selectedRange.electricalCharacteristicsFilters && this.selectedRange.electricalCharacteristicsFilters.length > 0) ||
          this.selectedRange.hideCharacteristicScreen;
      case OfferDataManagementConfigurationStep.FILTER:
        return this.selectedRange.visibleComponentsFilters && this.selectedRange.visibleComponentsFilters.length > 0;
      case OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC:
        return this.selectedRange.mainCharacteristics && this.selectedRange.mainCharacteristics.length === 2;
      case OfferDataManagementConfigurationStep.REFERENCE:
        return this.activeNext;
      default:
        return true;
    }
  }

  /**
   * Get step enum by number
   * @param number
   */
  private getEnumStep(number: number): OfferDataManagementConfigurationStep {
    switch (number) {
      case 0:
        return OfferDataManagementConfigurationStep.CHARACTERISTIC;
      case 1:
        return OfferDataManagementConfigurationStep.FILTER;
      case 2:
        return OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC;
      case 3:
        return OfferDataManagementConfigurationStep.DOCUMENT;
      case 4:
        return OfferDataManagementConfigurationStep.REFERENCE;
      case 5:
        return OfferDataManagementConfigurationStep.PUBLICATION;
    }
  }

  /**
   * Build the step list for offer configuration
   */
  private getStepList() {
    this.stepList = [
      {
        label: 'Electrical characteristics',
        className: this.getClassName(OfferDataManagementConfigurationStep.CHARACTERISTIC)
      },
      {label: 'Filter', className: this.getClassName(OfferDataManagementConfigurationStep.FILTER)},
      {
        label: 'Main characteristics',
        className: this.getClassName(OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC)
      },
    ];

    if (!(OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC && this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE)) {
      this.stepList.push({
        label: 'Documentation',
        className: this.getClassName(OfferDataManagementConfigurationStep.DOCUMENT)
      });
    }
    this.stepList.push(
      {label: 'References', className: this.getClassName(OfferDataManagementConfigurationStep.REFERENCE)},
      {label: 'Publication', className: this.getClassName(OfferDataManagementConfigurationStep.PUBLICATION)}
    );
  }

  /**
   * Get the css class for a specific step
   * @param step
   */
  private getClassName(step: OfferDataManagementConfigurationStep): String {
    if (this.currentStep === step) {
      return 'active';
    } else if (this.selectedRange && (parseInt(this.selectedRange.offerDataManagementConfigurationStep.toString(), 10) > step
      || this.selectedRange.status === RangeStatus.PUBLISHED)) {
      return 'complete';
    } else {
      return '';
    }
  }

  /**
   * Get the next or previous step according to the current step and the range type
   * @param currentStep
   * @param rangeType
   * @private
   */
  private getNextStep(currentStep: OfferDataManagementConfigurationStep, rangeType: RangeType): OfferDataManagementConfigurationStep {
    let increment = 1;
    if (currentStep === OfferDataManagementConfigurationStep.MAIN_CHARACTERISTIC && rangeType === RangeType.BUDJETARY_QUOTE) {
      increment = 2;
    }
    return this.getEnumStep(currentStep + increment);
  }

  /**
   * Get the next or previous step according to the current step and the range type
   * @param currentStep
   * @param rangeType
   * @private
   */
  private getPreviousStep(currentStep: OfferDataManagementConfigurationStep, rangeType: RangeType): OfferDataManagementConfigurationStep {
    let decrement = 1;
    if (currentStep === OfferDataManagementConfigurationStep.REFERENCE && rangeType === RangeType.BUDJETARY_QUOTE) {
      decrement = 2;
    }
    return this.getEnumStep(currentStep - decrement);
  }

  protected readonly RangeType = RangeType;

  isBudgetaryQuoteRange() {
    return this.selectedRange.rangeType === RangeType.BUDJETARY_QUOTE;
  }
}
